// eslint-disable-next-line
import { useState } from "react";

export default function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [showPw,   setShowPw]   = useState(false);

  const handleLogin = async () => {
    if (!username || !password) { setError("Enter username and password"); return; }
    setLoading(true); setError("");
    try {
      const r = await fetch("/auth/login", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({username,password}),
      });
      const d = await r.json();
      if (r.ok && d.token) { onLogin(d.token, d.user, d.role, d.envs); }
      else { setError(d.error || "Login failed"); }
    } catch { setError("Cannot reach auth server"); }
    setLoading(false);
  };

  const BASE = "#e0e5ec", SD = "#a3b1c6", SL = "#ffffff", TEXT = "#4a5568", TSUB = "#8b93a3", ACCENT = "#0284c7";
  const raised = `6px 6px 12px ${SD}, -6px -6px 12px ${SL}`;
  const raisedSm = `5px 5px 10px ${SD}, -5px -5px 10px ${SL}`;
  const inset = `inset 4px 4px 8px ${SD}, inset -4px -4px 8px ${SL}`;
  const inputStyle = { width:"100%", boxSizing:"border-box", background:BASE, border:"none",
    color:TEXT, borderRadius:12, padding:"13px 16px", fontSize:13, outline:"none",
    fontFamily:"'Inter',sans-serif", boxShadow:inset };

  return (
    <div style={{minHeight:"100vh", background:BASE, display:"flex", alignItems:"center",
      justifyContent:"center", fontFamily:"'Inter',-apple-system,sans-serif", padding:20,
      overflow:"hidden", position:"relative"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box;}
        @keyframes spinCW{from{transform:rotate(0)}to{transform:rotate(360deg)}}
        @keyframes spinCCW{from{transform:rotate(0)}to{transform:rotate(-360deg)}}
        @keyframes nfloatY{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
        @keyframes nbreath{0%,100%{box-shadow:6px 6px 12px ${SD},-6px -6px 12px ${SL}}50%{box-shadow:8px 8px 18px ${SD},-8px -8px 18px ${SL}}}
        @keyframes ncardIn{from{opacity:0;transform:translateY(24px) scale(.97)}to{opacity:1;transform:none}}
        @keyframes nfadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:none}}
        @keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}}
        @keyframes aiGlow{0%,100%{color:${ACCENT};text-shadow:0 0 8px rgba(2,132,199,0.3)}50%{color:#0369a1;text-shadow:0 0 16px rgba(2,132,199,0.5)}}
        .o1{animation:spinCW 40s linear infinite;}
        .o2{animation:spinCCW 55s linear infinite;}
        .o1 .chip{animation:spinCCW 40s linear infinite;}
        .o2 .chip{animation:spinCW 55s linear infinite;}
        .nlogo{animation:nfloatY 4s ease-in-out infinite, nbreath 4s ease-in-out infinite;}
        .ncard{animation:ncardIn .6s cubic-bezier(.2,.8,.2,1) both;}
        .ntitle{animation:nfadeUp .7s ease .1s both;}
        .nsub{animation:nfadeUp .7s ease .2s both;}
        .nf1{animation:nfadeUp .6s ease .3s both;}
        .nf2{animation:nfadeUp .6s ease .4s both;}
        .nbtn{animation:nfadeUp .6s ease .5s both;}
        .nai{animation:aiGlow 3s ease-in-out infinite;}
        .nerr{animation:shake .4s ease;}
        .ninput{transition:box-shadow .3s ease;}
        .ninput:focus{box-shadow:inset 5px 5px 10px ${SD}, inset -5px -5px 10px ${SL}, 0 0 0 2px rgba(2,132,199,0.25)!important;}
        .nbtnmain{transition:box-shadow .2s, transform .1s, color .2s;}
        .nbtnmain:hover{color:#0369a1!important;box-shadow:8px 8px 16px ${SD},-8px -8px 16px ${SL}!important;}
        .nbtnmain:active{transform:scale(.98);}
        .chip{position:absolute;transform:translate(-50%,-50%);font-size:10px;font-weight:700;
          font-family:'SF Mono',monospace;color:var(--c);background:${BASE};padding:5px 11px;
          border-radius:8px;box-shadow:3px 3px 6px ${SD},-3px -3px 6px ${SL};white-space:nowrap;}
        input::placeholder{color:#a8b0be;}
        input:-webkit-autofill{-webkit-box-shadow:0 0 0 30px ${BASE} inset!important;-webkit-text-fill-color:${TEXT}!important;}
      `}</style>

      {/* orbiting tech badges (no lines) */}
      <div style={{position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)",
        width:560, height:560, pointerEvents:"none"}}>
        <div className="orbit o1" style={{position:"absolute", inset:100, borderRadius:"50%"}}>
          <span className="chip" style={{"--c":"#0284c7", top:"-13px", left:"50%"}}>Linux</span>
          <span className="chip" style={{"--c":"#0284c7", bottom:"-13px", left:"50%"}}>Docker</span>
          <span className="chip" style={{"--c":"#0284c7", top:"50%", left:"-20px"}}>Kafka</span>
          <span className="chip" style={{"--c":"#0284c7", top:"50%", right:"-30px"}}>Redis</span>
        </div>
        <div className="orbit o2" style={{position:"absolute", inset:30, borderRadius:"50%"}}>
          <span className="chip" style={{"--c":"#0d9488", top:"-13px", left:"50%"}}>Jenkins</span>
          <span className="chip" style={{"--c":"#0d9488", bottom:"-13px", left:"50%"}}>MongoDB</span>
          <span className="chip" style={{"--c":"#0d9488", top:"50%", left:"-28px"}}>MQTT</span>
          <span className="chip" style={{"--c":"#0d9488", top:"50%", right:"-30px"}}>Nginx</span>
          <span className="chip" style={{"--c":"#0d9488", top:"14%", right:"2%"}}>Python</span>
          <span className="chip" style={{"--c":"#0d9488", bottom:"14%", left:"2%"}}>Java</span>
        </div>
      </div>

      {/* login card */}
      <div style={{width:340, position:"relative", zIndex:5}}>
        <div style={{textAlign:"center", marginBottom:26}}>
          <div className="nlogo" style={{width:72, height:72, borderRadius:20, background:BASE, margin:"0 auto 16px",
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:32}}>🛰️</div>
          <div className="ntitle" style={{fontSize:30, fontWeight:800, color:TEXT, letterSpacing:"-0.02em"}}>
            BN<span style={{color:ACCENT}}>360</span>
          </div>
          <div className="nsub" style={{fontSize:11, color:TSUB, letterSpacing:".1em", marginTop:6, fontWeight:600}}>
            <span className="nai" style={{fontWeight:800}}>AI-POWERED</span> · SERVICE MONITORING
          </div>
        </div>

        <div className="ncard" style={{background:BASE, borderRadius:24, padding:"30px 26px", boxShadow:raised}}>
          <div style={{fontSize:15, fontWeight:700, color:TEXT, marginBottom:22, textAlign:"center"}}>Sign in to continue</div>

          <div className="nf1" style={{marginBottom:16}}>
            <div style={{fontSize:11, color:TSUB, marginBottom:8, fontWeight:600, marginLeft:4}}>Username</div>
            <input className="ninput" value={username} onChange={e=>{setUsername(e.target.value);setError("");}}
              onKeyDown={e=>e.key==="Enter"&&handleLogin()} placeholder="Enter username" autoFocus style={inputStyle}/>
          </div>

          <div className="nf2" style={{marginBottom:22}}>
            <div style={{fontSize:11, color:TSUB, marginBottom:8, fontWeight:600, marginLeft:4}}>Password</div>
            <div style={{position:"relative"}}>
              <input className="ninput" type={showPw?"text":"password"} value={password}
                onChange={e=>{setPassword(e.target.value);setError("");}}
                onKeyDown={e=>e.key==="Enter"&&handleLogin()} placeholder="Enter password"
                style={{...inputStyle, paddingRight:44}}/>
              <button onClick={()=>setShowPw(v=>!v)} style={{position:"absolute", right:8, top:"50%",
                transform:"translateY(-50%)", width:30, height:30, borderRadius:9, background:BASE, border:"none",
                color:TSUB, cursor:"pointer", fontSize:13, display:"flex", alignItems:"center",
                justifyContent:"center", boxShadow:raisedSm}}>{showPw?"🙈":"👁"}</button>
            </div>
          </div>

          {error&&(
            <div className="nerr" style={{background:BASE, color:"#dc2626", borderRadius:10, padding:"10px 14px",
              fontSize:12, marginBottom:16, boxShadow:inset, fontWeight:500}}>⚠ {error}</div>
          )}

          <button className="nbtn nbtnmain" onClick={handleLogin} disabled={loading} style={{
            width:"100%", background:BASE, border:"none", color:loading?TSUB:ACCENT, borderRadius:12,
            padding:"14px", cursor:loading?"not-allowed":"pointer", fontFamily:"'Inter',sans-serif",
            fontSize:14, fontWeight:700, letterSpacing:".02em", boxShadow:loading?inset:raised}}>
            {loading ? "Signing in…" : "Sign In →"}
          </button>
        </div>

        <div style={{textAlign:"center", marginTop:20, fontSize:11, color:TSUB, letterSpacing:".04em"}}>
          © {new Date().getFullYear()} BN360 · Bluenett IOT &amp; Cloud Solutions
        </div>
      </div>
    </div>
  );
}
